import { ScrollPanelWithoutScrollbars } from '@simplywallst/ui-core';

import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';

import { formatterPercentage } from '@/utilities/format';
import { pluralise } from '@/utilities/pluralise';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';

import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import Tooltip from '@/components/Tooltip';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

interface Props {
  portfolio: Portfolio;
}

export const Metrics = ({ portfolio }: Props) => {
  const { holdings } = useHoldingsLimits({
    portfolioHoldings: portfolio.holdings,
  });

  const holdingOrHoldings = pluralise(holdings.length, 'holding', 'holdings');

  const {
    value,
    roi,
    roiValue,
    estimatedYearlyDividendFuture1y,
    estimatedFutureDividendYield,
  } = portfolio.analysis;

  return (
    <ScrollPanelWithoutScrollbars overflow="auto">
      <div role="list" className="flex flex-row items-end gap-x4 text-solid">
        <div className="grid grid-cols-[4px_max-content] gap-x1_25">
          <div className="w-x0_25 rounded-full bg-legacy-darkergray" />
          <div>
            <p className="m-0 text-xl font-medium" data-testid="total-value">
              {portfolio.isAnalysisRestricted
                ? 'n/a'
                : formatCurrencyToScaleTruncated(value, portfolio.currencyISO)}
            </p>
            <div className="m-0 text-tiny text-soft">
              <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                <Tooltip
                  title={
                    <div className="max-w-[260px]">
                      <p className="m-0 text-left text-tiny text-legacy-spacegray">
                        Current market value of your portfolio.
                      </p>
                    </div>
                  }
                  light
                  placement="top"
                  zIndex={99999}
                >
                  <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                    Total Value
                  </span>
                </Tooltip>
                •{' '}
                {portfolio.isAnalysisRestricted
                  ? '0 holdings'
                  : `${holdings.length} ${holdingOrHoldings}`}
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-[4px_max-content] gap-x1_25">
          <div className="w-x0_25 rounded-full bg-legacy-darkergray" />
          <div>
            <p className="m-0 text-xl font-medium" data-testid="roi-value">
              {portfolio.isAnalysisRestricted
                ? 'n/a'
                : formatCurrencyToScaleTruncated(
                    roiValue,
                    portfolio.currencyISO
                  )}
            </p>
            <div className="m-0 text-tiny text-soft">
              <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                <Tooltip
                  title={
                    <div className="max-w-[260px]">
                      <p className="m-0 text-left text-tiny text-legacy-spacegray">
                        Gains or losses on holdings currently held. (Tip: Create
                        a transactional portfolio for detailed returns.)
                      </p>
                    </div>
                  }
                  light
                  placement="top"
                  zIndex={99999}
                >
                  <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                    Unrealised Returns
                  </span>
                </Tooltip>
                {portfolio.isAnalysisRestricted ? null : (
                  <PositiveNegativeValue value={roi}>
                    {formatterPercentage(roi)}
                  </PositiveNegativeValue>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-[4px_max-content] gap-x1_25">
          <div className="w-x0_25 rounded-full bg-legacy-darkergray" />
          <div>
            <div className="grid grid-cols-[max-content_max-content] items-baseline gap-x0_5">
              <p className="m-0 text-xl font-medium" data-testid="dividend">
                {portfolio.isAnalysisRestricted
                  ? 'n/a'
                  : formatCurrencyToScaleTruncated(
                      estimatedYearlyDividendFuture1y,
                      portfolio.currencyISO
                    )}
              </p>
              <p className="m-0 text-base font-medium text-p-white-170">/yr</p>
            </div>
            <div className="m-0 text-tiny text-soft">
              <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                <Tooltip
                  title={
                    <div className="max-w-[260px]">
                      <p className="m-0 text-left text-tiny text-legacy-spacegray">
                        Expected future income from dividends over the next 12
                        months.
                      </p>
                    </div>
                  }
                  light
                  placement="top"
                  zIndex={99999}
                >
                  <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                    Est. Dividends
                  </span>
                </Tooltip>
                {portfolio.isAnalysisRestricted
                  ? null
                  : `• ${formatterPercentage(
                      estimatedFutureDividendYield
                    )} Yield`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollPanelWithoutScrollbars>
  );
};
